import React, { ComponentType } from 'react'
import { Image, ImageSourcePropType, ImageStyle, Pressable, StyleSheet, View } from 'react-native'
import { SvgProps } from 'react-native-svg'
import { DS, DSIcon } from '@/design-system'

import Card from '@/components/Mini/Card'
import { Badge } from '@/screens/play-screen/Sections/TodayLiveGames/Badge'

export const ROW_CARD_THUMB_WIDTH = 93
export const ROW_CARD_THUMB_HEIGHT = 79

export type RowCardBadge = {
  label: string
  backgroundColor?: string
  icon?: DSIcon
}

/** Raster artwork (PNG/JPG) placed on a solid background inside the 93x79 thumbnail */
export type RowCardThumbnailImage = {
  source: ImageSourcePropType
  backgroundColor?: string
  /** Size/offset of the image inside the thumbnail; defaults to covering it */
  style?: ImageStyle
}

type RowCardThumbnailProps =
  | {
      /** 93x79 SVG artwork shown on the left edge of the card */
      Thumbnail: ComponentType<SvgProps>
      thumbnailImage?: never
    }
  | {
      Thumbnail?: never
      /** Use for photo-like artwork: embedding rasters in SVGs does not render reliably in react-native-svg */
      thumbnailImage: RowCardThumbnailImage
    }

export type RowCardProps = RowCardThumbnailProps & {
  title: string
  description?: string
  badge?: RowCardBadge
  onPress: () => void
}

/** Horizontal list card: artwork on the left, title + chevron and description on the right. */
function RowCard({ title, description, Thumbnail, thumbnailImage, badge, onPress }: RowCardProps) {
  return (
    <Pressable onPress={onPress} accessibilityRole="button" accessibilityLabel={title}>
      <Card style={styles.card}>
        <View style={[styles.thumbnail, thumbnailImage && { backgroundColor: thumbnailImage.backgroundColor }]}>
          {Thumbnail ? (
            <Thumbnail width={ROW_CARD_THUMB_WIDTH} height={ROW_CARD_THUMB_HEIGHT} />
          ) : (
            thumbnailImage && (
              <Image
                source={thumbnailImage.source}
                resizeMode="cover"
                style={[styles.thumbnailImage, thumbnailImage.style]}
              />
            )
          )}
          {badge && (
            <View style={styles.badge}>
              <Badge label={badge.label} backgroundColor={badge.backgroundColor} icon={badge.icon} />
            </View>
          )}
        </View>

        <View style={styles.content}>
          <View style={styles.titleRow}>
            <DS.Text variant="Heading 6/Medium" numberOfLines={1} style={styles.title}>
              {title}
            </DS.Text>
            <View style={styles.chevron}>
              <DS.Icon name="Arrow--right-wide" size={[12, 6]} color="Global/Black" />
            </View>
          </View>
          {description !== undefined && (
            <DS.Text variant="Caption/Regular" color="Brand/Dark Grey">
              {description}
            </DS.Text>
          )}
        </View>
      </Card>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 0,
    paddingRight: DS.spacing[16],
    gap: DS.spacing[12],
    borderRadius: 12,
    overflow: 'hidden'
  },
  thumbnail: {
    width: ROW_CARD_THUMB_WIDTH,
    height: ROW_CARD_THUMB_HEIGHT,
    overflow: 'hidden'
  },
  thumbnailImage: {
    width: ROW_CARD_THUMB_WIDTH,
    height: ROW_CARD_THUMB_HEIGHT
  },
  badge: {
    position: 'absolute',
    top: 0,
    alignSelf: 'center'
  },
  content: {
    flex: 1,
    gap: DS.spacing[4]
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  title: {
    flexShrink: 1
  },
  // Keeps the 24x24 icon slot from the design around the 6x12 glyph
  chevron: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center'
  }
})

export default RowCard
